import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [verificationError, setVerificationError] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    // Check if token exists in localStorage
    const savedToken = localStorage.getItem('admin_token');
    if (savedToken) {
      verifyToken(savedToken);
    } else {
      setLoading(false);
    }
  }, []);

  const verifyToken = async (token) => {
    setVerificationError(false);
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/verify`, {
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUser(data);
        setToken(token);
      } else if (response.status === 401 || response.status === 403) {
        // Genuine authentication rejection — clear saved credentials.
        localStorage.removeItem('admin_token');
        setUser(null);
        setToken(null);
      } else {
        // 5xx / unexpected — server failure, not invalid credentials.
        setVerificationError(true);
      }
    } catch (error) {
      // Network failure / timeout — do NOT erase saved credentials.
      console.error('Token verification failed:', error);
      setVerificationError(true);
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      const formData = new URLSearchParams();
      formData.append('username', username);
      formData.append('password', password);

      const response = await fetch(`${BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setToken(data.access_token);
        localStorage.setItem('admin_token', data.access_token);

        // Verify token to get user data
        await verifyToken(data.access_token);
        return { success: true };
      } else {
        const error = await response.json();
        return { success: false, error: error.detail || 'Login failed' };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'Network error' };
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    setVerificationError(false);
    localStorage.removeItem('admin_token');
  };

  const retryVerification = () => {
    const savedToken = localStorage.getItem('admin_token');
    if (savedToken) {
      verifyToken(savedToken);
    }
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, verificationError, login, logout, retryVerification }}>
      {children}
    </AuthContext.Provider>
  );
};
